import React from "react";
import { FiClock, FiLoader, FiAlertTriangle, FiCheck, FiX, FiHelpCircle } from "react-icons/fi";

import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    pending_badge: "Pending",
    running_badge: "Running…",
    error_badge: "Error",
    no_run: "No screening run yet for this candidate.",
    waiting: "Waiting to be processed…",
    screening: "Screening resume…",
    failed: "Processing failed",
    reasoning: "Reasoning",
    score: "Score",
    eliminated: "Eliminated",
    points_of: "{{got}} points out of {{max}}",
    musts_failed: "{{count}} must-have missed",
    musts: "Must-haves",
    musts_count: "{{passed}} of {{total}} met",
    weighted: "Scored criteria",
    weighted_count: "{{got}} / {{max}} points",
    not_counted: "Not counted",
    pass: "Met",
    fail: "Not met",
    unclear: "Unclear",
    high: "Very important",
    medium: "Important",
    low: "Bonus",
    processed: "Processed",
    half_points: "Unclear = half the points",
  },
  fr: {
    pending_badge: "En attente",
    running_badge: "En cours…",
    error_badge: "Erreur",
    no_run: "Aucun screening lancé pour ce candidat.",
    waiting: "En attente de traitement…",
    screening: "Analyse du CV…",
    failed: "Le traitement a échoué",
    reasoning: "Raisonnement",
    score: "Score",
    eliminated: "Écarté",
    points_of: "{{got}} points sur {{max}}",
    musts_failed: "{{count}} indispensable non rempli",
    musts: "Indispensables",
    musts_count: "{{passed}} sur {{total}} remplis",
    weighted: "Critères notés",
    weighted_count: "{{got}} / {{max}} points",
    not_counted: "Non comptés",
    pass: "Rempli",
    fail: "Non rempli",
    unclear: "Pas clair",
    high: "Très important",
    medium: "Important",
    low: "Bonus",
    processed: "Calculé le",
    half_points: "Pas clair = la moitié des points",
  },
};

const CHECK_STATUS_CONFIG = {
  pass: { icon: FiCheck, bg: "bg-green-100", text: "text-green-600", tag: "bg-green-100 text-green-700", bar: "bg-green-600" },
  fail: { icon: FiX, bg: "bg-red-100", text: "text-red-600", tag: "bg-red-100 text-red-700", bar: "bg-red-600" },
  unclear: { icon: FiHelpCircle, bg: "bg-amber-100", text: "text-amber-600", tag: "bg-amber-100 text-amber-700", bar: "bg-amber-500" },
};

const formatPoints = (n) => String(n).replace(".", ",");

function scoreTheme(score, eliminated) {
  if (eliminated) return { card: "bg-red-50 border-red-200", side: "bg-red-100 border-red-200", label: "text-red-700", text: "text-red-900" };
  if (score >= 80) return { card: "bg-emerald-50 border-emerald-200", side: "bg-emerald-100 border-emerald-200", label: "text-emerald-700", text: "text-emerald-900" };
  if (score >= 60) return { card: "bg-blue-50 border-blue-200", side: "bg-blue-100 border-blue-200", label: "text-blue-700", text: "text-blue-900" };
  if (score >= 40) return { card: "bg-amber-50 border-amber-200", side: "bg-amber-100 border-amber-200", label: "text-amber-700", text: "text-amber-900" };
  return { card: "bg-gray-50 border-gray-200", side: "bg-gray-100 border-gray-200", label: "text-gray-600", text: "text-gray-900" };
}

function StatusBadge({ status, t }) {
  if (status === "pending")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <FiClock className="w-3 h-3" /> {t("pending_badge")}
      </span>
    );
  if (status === "in_progress")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
        <FiLoader className="w-3 h-3 animate-spin" /> {t("running_badge")}
      </span>
    );
  if (status === "error")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <FiAlertTriangle className="w-3 h-3" /> {t("error_badge")}
      </span>
    );
  return null;
}

function Summary({ application, musts, weighted, t }) {
  const eliminated = application.screening_eliminated;
  const theme = scoreTheme(application.screening_score, eliminated);
  const got = weighted.reduce((sum, c) => sum + (c.points || 0), 0);
  const max = weighted.reduce((sum, c) => sum + (c.max_points || 0), 0);
  const failedMusts = musts.filter((c) => c.status === "fail").length;

  return (
    <div className={`flex flex-wrap rounded-xl border overflow-hidden ${theme.card}`}>
      <div className="flex-[999_1_360px] min-w-0 px-5 py-4">
        <div className={`text-xs font-semibold uppercase tracking-wide ${theme.label}`}>{t("reasoning")}</div>
        <p className={`text-sm leading-relaxed mt-1.5 ${theme.text}`}>{application.screening_explanation}</p>
      </div>
      <div className={`flex-[1_0_180px] flex flex-col items-center justify-center gap-1 px-5 py-4 border-l ${theme.side}`}>
        <div className={`text-xs font-semibold uppercase tracking-wide ${theme.label}`}>{t("score")}</div>
        {eliminated ? (
          <>
            <span className={`text-2xl font-extrabold ${theme.text}`}>{t("eliminated")}</span>
            <span className={`text-xs ${theme.label}`}>{t("musts_failed", { count: failedMusts })}</span>
          </>
        ) : (
          <>
            <span className={`flex items-baseline gap-1 ${theme.text}`}>
              <span className="text-4xl font-extrabold tracking-tight">{application.screening_score}</span>
              <span className={`text-sm ${theme.label}`}>/ 100</span>
            </span>
            {max > 0 && <span className={`text-xs ${theme.label}`}>{t("points_of", { got: formatPoints(got), max })}</span>}
          </>
        )}
      </div>
    </div>
  );
}

function CheckRow({ check, right, dimmed, t }) {
  const config = CHECK_STATUS_CONFIG[check.status] || CHECK_STATUS_CONFIG.unclear;
  const Icon = config.icon;
  return (
    <div className={`flex items-start gap-3 py-3 ${dimmed ? "opacity-60" : ""}`}>
      <div className={`w-6 h-6 rounded-full ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className={`w-3.5 h-3.5 ${config.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-gray-900">{check.label}</span>
        {check.importance !== "must" && (
          <span className="ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px] font-semibold align-[1px]">{t(check.importance)}</span>
        )}
        {check.comment && <span className="text-sm text-gray-500 ml-1.5">— {check.comment}</span>}
      </div>
      <div className="w-28 shrink-0 flex flex-col items-end gap-1.5">{right}</div>
    </div>
  );
}

export default function AppAiScoring({ application }) {
  const { t } = useComponentTranslation(translations);
  if (!application) return null;

  const status = application.screening_status;
  const checks = application.screening_checks || [];
  const musts = checks.filter((c) => c.importance === "must");
  const weighted = checks.filter((c) => c.importance !== "must");
  const eliminated = application.screening_eliminated;

  if (!status && application.screening_score == null) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <p className="text-sm text-gray-500">{t("no_run")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {status !== "done" && <StatusBadge status={status} t={t} />}

      {(status === "pending" || status === "in_progress") && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className={`w-10 h-10 rounded-full mx-auto mb-3 flex items-center justify-center ${status === "pending" ? "bg-amber-100" : "bg-indigo-100"}`}>
            {status === "pending" ? <FiClock className="w-5 h-5 text-amber-600" /> : <FiLoader className="w-5 h-5 text-indigo-600 animate-spin" />}
          </div>
          <p className="text-sm text-gray-600">{status === "pending" ? t("waiting") : t("screening")}</p>
        </div>
      )}

      {status === "error" && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{t("failed")}</p>
              {application.screening_error && <p className="text-xs text-red-600 mt-1">{application.screening_error}</p>}
            </div>
          </div>
        </div>
      )}

      {status === "done" && (
        <div className="space-y-6">
          <Summary application={application} musts={musts} weighted={weighted} t={t} />

          {musts.length > 0 && (
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("musts")}</h3>
                <span className={`text-xs font-semibold ${eliminated ? "text-red-700" : "text-emerald-700"}`}>
                  {t("musts_count", { passed: musts.filter((c) => c.status === "pass").length, total: musts.length })}
                </span>
              </div>
              <div className="divide-y divide-gray-100 border-t border-gray-100">
                {musts.map((check, i) => {
                  const config = CHECK_STATUS_CONFIG[check.status] || CHECK_STATUS_CONFIG.unclear;
                  return (
                    <CheckRow
                      key={i}
                      check={check}
                      t={t}
                      right={<span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${config.tag}`}>{t(check.status)}</span>}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {weighted.length > 0 && (
            <div>
              <div className="flex justify-between items-baseline mb-1">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("weighted")}</h3>
                <span className="text-xs font-semibold text-gray-700">
                  {eliminated
                    ? t("not_counted")
                    : t("weighted_count", {
                        got: formatPoints(weighted.reduce((sum, c) => sum + (c.points || 0), 0)),
                        max: weighted.reduce((sum, c) => sum + (c.max_points || 0), 0),
                      })}
                </span>
              </div>
              <div className="divide-y divide-gray-100 border-t border-gray-100">
                {weighted.map((check, i) => {
                  const config = CHECK_STATUS_CONFIG[check.status] || CHECK_STATUS_CONFIG.unclear;
                  const ratio = check.max_points ? (check.points || 0) / check.max_points : 0;
                  return (
                    <CheckRow
                      key={i}
                      check={check}
                      t={t}
                      dimmed={eliminated}
                      right={
                        eliminated ? (
                          <span className="text-sm text-gray-400">– / {check.max_points}</span>
                        ) : (
                          <>
                            <span className="text-sm font-bold text-gray-900">
                              {formatPoints(check.points)} / {check.max_points}
                            </span>
                            <span className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
                              <span className={`block h-full ${config.bar}`} style={{ width: `${ratio * 100}%` }} />
                            </span>
                          </>
                        )
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          {application.screening_updated_at && (
            <p className="text-xs text-gray-400 text-right">{t("processed")} {new Date(application.screening_updated_at).toLocaleString()} · {t("half_points")}</p>
          )}
        </div>
      )}
    </div>
  );
}
